import React, { useRef, useEffect, useState } from "react";

// Constants for Drone Grid
const FLASH_INTERVAL_MS = 500; // Interval for flashing effect
// Simplified static colors for debugging
const STATION_COLOR_INACTIVE = "rgba(0, 180, 255, 0.9)"; // Standard blue
const STATION_COLOR_ACTIVE = "rgba(255, 255, 0, 1.0)"; // Bright Yellow for active
const STATION_RADIUS_PX = 5; // Size of the station dot
const DRONE_COLOR = "rgba(255, 0, 0, 0.9)"; // Red for active drones
const DRONE_RADIUS_PX = 4; // Size of the drone dot
const DRONE_SPEED_TILES_PER_HOUR = 90;
const TILES_PER_GRID_CELL = 1; // Assuming 1 tile = 1 grid cell for speed calculation

// Simulation grid size (must match SimulationOverlay)
const GRID_SIZE_PX = 12;

// --- Helper Functions ---
// Calculate distance between two grid points
const calculateDistance = (p1, p2) => {
  return Math.sqrt(Math.pow(p1.row - p2.row, 2) + Math.pow(p1.col - p2.col, 2));
};

// Convert simulation time (seconds) to hours
const simSecondsToHours = (seconds) => seconds / 3600;

// --- Component ---
const DroneGrid = ({
  gridDimensions,
  droneModeActive,
  zIndexValue,
  // fireLocation prop removed
  simulationTime, // Current simulation time in seconds (needed for position calculation)
  // onDroneArrived prop removed
  droneStations, // Array of { row: number, col: number } passed from App
  activeMissions, // Array of mission objects passed from App
}) => {
  const canvasRef = useRef(null);
  const [flashToggle, setFlashToggle] = useState(true); // State to force re-renders

  // Refs to hold the latest prop values for use inside the animation loop
  const gridDimensionsRef = useRef(gridDimensions);
  const droneModeActiveRef = useRef(droneModeActive);
  const droneStationsRef = useRef(droneStations);
  const activeMissionsRef = useRef(activeMissions);
  const simulationTimeRef = useRef(simulationTime);
  const flashToggleRef = useRef(flashToggle); // Ref to hold the latest flash state

  // Effect to update refs when props change
  useEffect(() => {
    gridDimensionsRef.current = gridDimensions;
    droneModeActiveRef.current = droneModeActive;
    droneStationsRef.current = droneStations;
    activeMissionsRef.current = activeMissions;
    simulationTimeRef.current = simulationTime;
    flashToggleRef.current = flashToggle; // Update flash toggle ref when state changes
  }, [
    gridDimensions,
    droneModeActive,
    droneStations,
    activeMissions,
    simulationTime,
    flashToggle, // Add flashToggle as dependency here
  ]);

  // Effect to toggle the flash state
  useEffect(() => {
    const intervalId = setInterval(() => {
      setFlashToggle((prev) => !prev);
    }, FLASH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []); // Run only once on mount

  // --- Animation Loop ---
  useEffect(() => {
    // Use requestAnimationFrame for smoother animation
    let animationFrameId;

    const renderLoop = (timestamp) => {
      // Resolve the canvas from the ref every frame — React may replace the
      // element across remounts and a captured reference would go stale.
      const canvas = canvasRef.current;
      if (!canvas) {
        animationFrameId = requestAnimationFrame(renderLoop);
        return;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        animationFrameId = requestAnimationFrame(renderLoop);
        return;
      }
      // timestamp is provided by requestAnimationFrame
      // Access latest props via refs inside the loop
      const currentGridDimensions = gridDimensionsRef.current;
      const currentDroneModeActive = droneModeActiveRef.current;
      const currentDroneStations = droneStationsRef.current;
      const currentActiveMissions = activeMissionsRef.current;
      const currentSimulationTime = simulationTimeRef.current;

      // --- Drawing logic starts here ---
      if (!currentGridDimensions.width || !currentGridDimensions.height) {
        animationFrameId = requestAnimationFrame(renderLoop); // Keep loop running even if dimensions are 0 temporarily
        return;
      }

      const canvasWidth = currentGridDimensions.width;
      const canvasHeight = currentGridDimensions.height;

      // Ensure canvas size matches dimensions
      if (canvas.width !== canvasWidth) canvas.width = canvasWidth;
      if (canvas.height !== canvasHeight) canvas.height = canvasHeight;

      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // 1. Draw Stations (using refs), check for active status
      if (
        currentDroneModeActive &&
        currentDroneStations &&
        currentDroneStations.length > 0
      ) {
        const activeStationCoords = new Set(
          (currentActiveMissions || []) // Use ref value
            .filter((m) => m.origin) // Ensure origin exists
            .map((m) => `${m.origin.row},${m.origin.col}`)
        );

        currentDroneStations.forEach(({ row, col }) => {
          // Use ref value
          const isStationActive = activeStationCoords.has(`${row},${col}`);
          let stationFillStyle = STATION_COLOR_INACTIVE; // Default to inactive
          const stationRadius = STATION_RADIUS_PX; // Use fixed radius

          // Set fillStyle based on active status and the flashToggle state
          if (isStationActive) {
            // Use the flashToggle state (via ref) to determine color
            stationFillStyle = flashToggleRef.current
              ? STATION_COLOR_ACTIVE
              : STATION_COLOR_INACTIVE;
          } else {
            stationFillStyle = STATION_COLOR_INACTIVE; // Inactive stations remain blue
          }

          ctx.fillStyle = stationFillStyle; // Apply the determined color

          const centerX = col * GRID_SIZE_PX + GRID_SIZE_PX / 2;
          const centerY = row * GRID_SIZE_PX + GRID_SIZE_PX / 2;
          ctx.beginPath();
          // Use the fixed radius
          ctx.arc(centerX, centerY, stationRadius, 0, 2 * Math.PI);
          ctx.fill();
        });
      }

      // 2. Draw Active Drones based on Missions (using refs) - Unchanged logic, just using refs
      if (
        currentDroneModeActive &&
        currentActiveMissions &&
        currentActiveMissions.length > 0
      ) {
        ctx.fillStyle = DRONE_COLOR;
        const currentSimTimeSeconds = Math.floor(currentSimulationTime / 1000); // Use ref value

        currentActiveMissions.forEach((mission) => {
          // Use ref value
          let currentPos = mission.origin; // Default to origin

          if (mission.status === "deploying") {
            const elapsedTimeSimSeconds =
              currentSimTimeSeconds - Math.floor(mission.startTime / 1000); // Compare seconds
            const elapsedTimeHours = simSecondsToHours(elapsedTimeSimSeconds);
            const distanceCovered =
              elapsedTimeHours *
              DRONE_SPEED_TILES_PER_HOUR *
              TILES_PER_GRID_CELL;
            const totalDistance = calculateDistance(
              mission.origin,
              mission.target
            );

            if (distanceCovered >= totalDistance) {
              currentPos = mission.target; // Snap to target if arrived
            } else if (totalDistance > 0) {
              // Calculate intermediate position
              const fractionTravelled = distanceCovered / totalDistance;
              currentPos = {
                row:
                  mission.origin.row +
                  (mission.target.row - mission.origin.row) * fractionTravelled,
                col:
                  mission.origin.col +
                  (mission.target.col - mission.origin.col) * fractionTravelled,
              };
            }
          } else if (mission.status === "arrived") {
            currentPos = mission.target; // Stay at target if arrived
          }
          // Add logic for 'returning' status if needed later

          // Draw drone at its current calculated position
          const centerX = currentPos.col * GRID_SIZE_PX + GRID_SIZE_PX / 2;
          const centerY = currentPos.row * GRID_SIZE_PX + GRID_SIZE_PX / 2;
          ctx.beginPath();
          ctx.arc(centerX, centerY, DRONE_RADIUS_PX, 0, 2 * Math.PI);
          ctx.fill();
        });
      }
      // --- Drawing logic ends here ---

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    // Request the first frame
    animationFrameId = requestAnimationFrame(renderLoop);

    return () => cancelAnimationFrame(animationFrameId); // Cleanup on unmount
  }, []); // Empty dependency array: Run effect only once on mount

  // The actual component return statement
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        // objectFit: "contain", // Removed objectFit, not standard for canvas
        pointerEvents: "none",
        zIndex: zIndexValue,
      }}
      // Width/height attributes are set dynamically by the drawing logic inside useEffect
    />
  );
};

export default DroneGrid;
